import { Injectable } from '@angular/core';
import { ApiHttpClient, ListOption, ListOptionRequest } from '../modules/tcm-core.module';
import { ClinicListOptionForProviderRequest, ClinicProductThresholdModel } from '../models/clinics';
import { UpdateClinicLogoRequest } from '../models/clinics/update-clinic-logo-request';
import { ClinicOptionWithIdentifier } from '../models/users';
import { ClinicLocationRequest } from '../models/clinics/fortis/clinic-location-request';
import { FortisBaseResponse } from '../models/clinics/fortis/fortis-base-response';
import { FileResponse } from '../modules/tcm-core.module/models/files/file-response';

@Injectable({
  providedIn: 'root'
})
export class ClinicDataService {
  constructor(private readonly httpClient: ApiHttpClient) {
  }

  public getClinicOptionsAsync(query: string, cancellationToken: Promise<any>): Promise<ListOption<number>[]> {
    const request: ListOptionRequest = { searchString: query };
    return this.httpClient.getJsonAsync<ListOption<number>[]>('clinics/options', request, cancellationToken);
  }

  public getAllClinicOptionsAsync(cancellationToken: Promise<any>): Promise<ListOption<number>[]> {
    return this.httpClient.getJsonAsync<ListOption<number>[]>('clinics/GetAllClinicOptions', null, cancellationToken);
  }

  public getDefaultClinicIfExists(cancellationToken: Promise<any>): Promise<number> {
    return this.httpClient.getJsonAsync<number>('clinics/GetDefaultClinicIfExists', null, cancellationToken);
  }

  public getClinicOptionsForProviderAsync(query: string, providerId: number, cancellationToken: Promise<any>): Promise<ListOption<number>[]> {
    const request: ClinicListOptionForProviderRequest = { searchString: query, providerId: providerId };
    return this.httpClient.getJsonAsync<ListOption<number>[]>('clinics/optionsForProvider', request, cancellationToken);
  }

  public getClinicOptionsWithIdentifierAsync(query: string, cancellationToken: Promise<any>): Promise<ClinicOptionWithIdentifier[]> {
    const request: ListOptionRequest = { searchString: query };
    return this.httpClient.getJsonAsync<ClinicOptionWithIdentifier[]>('clinics/optionsWithIdentifier', request, cancellationToken);
  }

  public checkCodeExistsAsync(code: string, clinicId: number, timeout: Promise<any>): Promise<boolean> {
    const request = { code: code, clinicId: clinicId };
    return this.httpClient.getJsonAsync<boolean>('clinics/checkCodeExists', request, timeout, false);
  }

  public saveInventoryThresholds(clinicId: number, inventoryThresholds: ClinicProductThresholdModel[], timeout: Promise<any>): Promise<boolean> {
    const request = inventoryThresholds;
    return this.httpClient.postJsonAsync<boolean>('clinics/saveInventoryThresholds?clinicId=' + clinicId, request, timeout, false);
  }

  public deleteInventoryThresholdLineAsync(id: number, timeout: Promise<any>): Promise<any> {
    return this.httpClient.postJsonAsync('Clinics/DeleteInventoryThreshold', id, timeout);
  }

  public updateClinicLogoAsync(request: UpdateClinicLogoRequest): Promise<any> {
    return this.httpClient.postJsonAsync('ClinicWidget/UpdateClinicLogo', request);
  }

  public checkErpIdExistsAsync(erpId: any, clinicId?: number, timeout?: Promise<any>): Promise<boolean> {
    const request = { erpId: erpId, clinicId: clinicId };

    return this.httpClient.getJsonAsync<boolean>('Clinics/CheckClinicErpIdExist', request, timeout);
  }

  public obonoRenew(clinicId?: number, timeout?: Promise<any>): Promise<boolean> {
    const request = { clinicId: clinicId };

    return this.httpClient.getJsonAsync<boolean>('Clinics/ObonoRenew', request, timeout);
  }

  public checkEmailAddressExistsAsync(clinicId?: number, timeout?: Promise<any>): Promise<boolean> {
    const request = { clinicId: clinicId };

    return this.httpClient.getJsonAsync<boolean>('Clinics/CheckClinicEmailAddressExist', request, timeout);
  }
  public obonoAudit(clinicId: number, startdate?: string, enddate?: string, timeout?: Promise<any>): Promise<string> {
    const request = { clinicId: clinicId, FromDate: startdate, ToDate: enddate };
    return this.httpClient.getJsonAsync<string>('Clinics/ObonoAudit', request, timeout);
  }

  public closeCashRegister(clinicId: number, startdate?: string, enddate?: string, timeout?: Promise<any>): Promise<FileResponse> {
    const request = { clinicId: clinicId, FromDate: startdate, ToDate: enddate };

    return this.httpClient.getFileAsync('Clinics/CloseCashRegister', request, timeout);
  }

  // Fortis

  public pingFortisTerminalAsync(id: number, timeout: Promise<any>): Promise<FortisBaseResponse<any>> {
    const request = { clinicId: id };
    return this.httpClient.getJsonAsync<FortisBaseResponse<any>>('Clinics/PingFortisTerminal', request, timeout);
  }

  public getFortisLocationInfo(request: ClinicLocationRequest, timeout?: Promise<any>): Promise<FortisBaseResponse<any>> {
    return this.httpClient.postJsonAsync<FortisBaseResponse<any>>('Clinics/GetFortisLocationInfo', request, timeout);
  }

  public removeFortisSetupAsync(id: number, timeout: Promise<any>): Promise<any> {
    return this.httpClient.postJsonAsync('Clinics/RemoveFortisSetup', id, timeout);
  }

}
